import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import IDCard from '../components/IDCard';
import AppendexCard from '../components/AppendexCard';
import { Sparkles, Edit, FileText, ScrollText } from 'lucide-react';

const IAView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [instance, setInstance] = useState<any>(null);
  const [appendexList, setAppendexList] = useState<any[]>([]);

  useEffect(() => {
    const loadInstance = async () => {
      if (!id) {
        navigate('/');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('ia_instances')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (error || !data) {
          console.error('Erreur chargement instance:', error);
          navigate('/');
          return;
        }

        setInstance(data);

        const { data: appendexData, error: appendexError } = await supabase
          .from('appendex_interactions')
          .select('*')
          .eq('ia_instance_id', id)
          .order('created_at', { ascending: false });

        if (!appendexError && appendexData) {
          setAppendexList(appendexData);
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    loadInstance();
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 flex items-center justify-center">
        <div className="animate-pulse text-steel-600">
          <Sparkles className="h-12 w-12 mx-auto mb-4" />
          <p>Chargement de l'instance...</p>
        </div>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 flex items-center justify-center">
        <div className="text-center text-steel-600">
          <p>Instance non trouvée</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 bg-steel text-white px-6 py-2 rounded-lg hover:bg-steel/90 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const canEdit = user && (user.id === instance.user_id || user.role === 'admin');

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="h-8 w-8 text-copper" />
            <h1 className="text-3xl font-bold text-steel">Instance IA Certifiée</h1>
          </div>
          <p className="text-stone">Carte d'identité numérique officielle MemoMundi.ai</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="flex flex-col items-center lg:items-start">
            <IDCard
              instance={instance}
              className="w-full max-w-md animate-slide-up"
              onDeactivate={(updatedInstance) => setInstance(updatedInstance)}
            />

            <div className="mt-6 space-y-3 w-full max-w-md">
              {canEdit && (
                <button
                  onClick={() => navigate(`/ia/${id}/edit`)}
                  className="w-full bg-steel text-white px-6 py-3 rounded-lg hover:bg-steel/90 transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <Edit className="h-5 w-5" />
                  <span>Modifier l'instance</span>
                </button>
              )}

              <button
                onClick={() => navigate('/appendex/create')}
                className="w-full bg-copper text-white px-6 py-3 rounded-lg hover:bg-copper/90 transition-colors inline-flex items-center justify-center space-x-2"
              >
                <FileText className="h-5 w-5" />
                <span>Créer un Appendex</span>
              </button>
            </div>
          </div>

          <div className="space-y-6 animate-fade-in">
            {instance.description && (
              <div className="bg-white border-2 border-stone-300 rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-2 mb-4 border-b border-stone-200 pb-3">
                  <ScrollText className="h-6 w-6 text-copper" />
                  <h2 className="text-xl font-bold text-steel">Crédit Public</h2>
                </div>
                <div className="text-steel-700 whitespace-pre-wrap leading-relaxed">
                  {instance.description}
                </div>
              </div>
            )}

            <div className="bg-white border-2 border-stone-300 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4 border-b border-stone-200 pb-3">
                <div className="flex items-center space-x-2">
                  <FileText className="h-6 w-6 text-copper" />
                  <h2 className="text-xl font-bold text-steel">Appendex</h2>
                </div>
                <span className="bg-copper-100 text-copper-700 px-3 py-1 rounded-full text-sm font-semibold">
                  {appendexList.length}
                </span>
              </div>

              {appendexList.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 mb-4">Aucun Appendex pour cette instance</p>
                  <button
                    onClick={() => navigate('/appendex/create')}
                    className="bg-copper text-white px-4 py-2 rounded-lg hover:bg-copper/90 transition-colors text-sm"
                  >
                    Créer le premier Appendex
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {appendexList.map((appendex) => (
                    <AppendexCard
                      key={appendex.id}
                      appendex={appendex}
                      onClick={() => navigate(`/appendex/${appendex.id}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IAView;
